/**
 * Delete a Turso sync-wasm database and every sidecar it writes, from the OPFS root.
 *
 * The wasm engine stores `<name>` plus `<name>-wal` / `<name>-changes` /
 * `<name>-wal-revert` / `<name>-info` at the OPFS root (the exact set varies by
 * engine version and by how far a `connect()` got). A NON-EMPTY set is what makes
 * the next open skip `bootstrapIfEmpty`, so leaving any of them behind is what
 * produces the "connected but syncs in neither direction" state.
 *
 * Matching is by prefix rather than a fixed suffix list so a sidecar this build
 * doesn't know about still gets cleared. `<name>` itself, `<name>-*` and
 * `<name>.*` all match; an unrelated database whose name merely starts with the
 * same characters does not (the delimiter check keeps `actor.db` from matching
 * `actor.db2`… while still matching `actor.db-wal`).
 *
 * Best-effort by design: a locked or already-missing entry must not abort the
 * caller (a retry, or a sign-in reset). OPFS refuses `removeEntry` while a sync
 * access handle is open, which is why callers must CLOSE the connection first.
 */
export async function wipeTursoDatabaseFiles(databaseName: string): Promise<string[]> {
  const storage = globalThis.navigator?.storage;
  if (storage?.getDirectory == null) {
    return [];
  }
  const root = await storage.getDirectory();

  const toRemove: string[] = [];
  // `entries()` is async-iterable on OPFS dir handles.
  for await (const [name] of root.entries()) {
    if (isTursoDatabaseFile(name, databaseName)) {
      toRemove.push(name);
    }
  }

  const removed: string[] = [];
  await Promise.all(
    toRemove.map(async (name) => {
      try {
        await root.removeEntry(name, { recursive: true });
        removed.push(name);
      } catch {
        // best-effort: a locked/already-gone entry must not block the caller.
      }
    }),
  );
  return removed;
}

/** The main file, or one of its sidecars (`-wal`, `-info`, …). */
function isTursoDatabaseFile(entryName: string, databaseName: string): boolean {
  return (
    entryName === databaseName ||
    entryName.startsWith(`${databaseName}-`) ||
    entryName.startsWith(`${databaseName}.`)
  );
}
